import { BrainCircuit, BarChart3, DollarSign } from "lucide-react";
import { MODULES } from "./pricingContent";

export const COMPARE_TOOLS = [
    {
        id: "apex",
        name: "ApexBrain",
        slug: "apexbrain",
        tag: "operating layer",
        accent: "text-electric",
        accentBg: "bg-electric/10 border-electric/30",
        ring: "from-electric/30 to-electric/0",
        gradient: "from-electric/15 via-cyan-400/10 to-emerald-400/10",
        scope: "Whole company",
        price: "from ₹0",
        seatMath: "Free under 8 seats",
        bullets: [
            "Sales, HR, Projects, Finance, Documents & Communications in one graph",
            "Cross-module AI Co-pilot with citations",
            "Workflows, dashboards and chat live on the same record",
            "Quarterly outcomes, not just task ticks",
        ],
        crown: true,
    },
    {
        id: "jira",
        name: "Jira",
        slug: "jira",
        tag: "engineering tickets",
        accent: "text-blue-400",
        accentBg: "bg-blue-400/10 border-blue-400/25",
        ring: "from-blue-400/30 to-blue-400/0",
        gradient: "from-blue-500/15 to-blue-700/10",
        scope: "Engineering only",
        price: "from $7.16 / seat",
        seatMath: "Per editor seat",
        bullets: [
            "Mature engineering issue tracker built around tickets",
            "Powerful JQL search but heavy admin overhead",
            "Limited reach outside engineering teams",
            "AI add-ons gated behind premium tiers",
        ],
    },
    {
        id: "monday",
        name: "Monday",
        slug: "monday",
        logo: "/logos/monday.svg",
        tag: "colourful boards",
        accent: "text-rose-300",
        accentBg: "bg-rose-300/10 border-rose-300/25",
        ring: "from-rose-400/30 to-rose-400/0",
        gradient: "from-rose-500/15 to-rose-700/10",
        scope: "Work boards",
        price: "from $9 / seat",
        seatMath: "3-seat minimum",
        bullets: [
            "Friendly board UI, great for project tracking",
            "Add-ons for CRM and HR sold as separate products",
            "Cross-product data lives on separate boards",
            "Limited finance / company-level reasoning",
        ],
    },
    {
        id: "clickup",
        name: "ClickUp",
        slug: "clickup",
        tag: "everything-but",
        accent: "text-violet-300",
        accentBg: "bg-violet-300/10 border-violet-300/25",
        ring: "from-violet-400/30 to-violet-400/0",
        gradient: "from-violet-500/15 to-violet-700/10",
        scope: "Tasks + docs",
        price: "from $7 / seat",
        seatMath: "AI is +$5 / seat add-on",
        bullets: [
            "Wide feature surface area, steep learning curve",
            "Frequent UI churn — power users only",
            "AI assistant is a paid upgrade with quotas",
            "Reporting at department level, not company level",
        ],
    },
];

const COMPETITOR_DEFAULTS = {
    dashboard: { jira: "no", monday: "partial", clickup: "partial" },
    sales: { jira: "no", monday: "addon", clickup: "no" },
    hr: { jira: "no", monday: "addon", clickup: "no" },
    projects: { jira: "yes", monday: "yes", clickup: "yes" },
    finance: { jira: "no", monday: "no", clickup: "no" },
    documents: { jira: "addon", monday: "partial", clickup: "yes" },
    communications: { jira: "addon", monday: "partial", clickup: "yes" },
    analytics: { jira: "no", monday: "partial", clickup: "partial" },
    ai: { jira: "no", monday: "no", clickup: "addon" },
};

const FEATURE_OVERRIDES = {
    projects: {
        Wiki: { jira: "addon", monday: "partial", clickup: "yes" },
        Knowledgebase: { jira: "addon", monday: "partial", clickup: "yes" },
        Whiteboards: { jira: "no", monday: "partial", clickup: "yes" },
        Scheduling: { jira: "partial", monday: "partial", clickup: "partial" },
        "Workload Management": {
            jira: "partial",
            monday: "partial",
            clickup: "partial",
        },
    },
};

const moduleMatrixRows = (mod) =>
    mod.features.map((feature) => ({
        label: feature,
        apex: "yes",
        ...(FEATURE_OVERRIDES[mod.id]?.[feature] ??
            COMPETITOR_DEFAULTS[mod.id] ?? {
                jira: "no",
                monday: "no",
                clickup: "no",
            }),
    }));

export const COMPARE_CATEGORIES = [
    ...MODULES.map((mod) => ({
        icon: mod.icon,
        name: mod.name,
        moduleId: mod.id,
        rows: moduleMatrixRows(mod),
    })),
    {
        icon: BrainCircuit,
        name: "Intelligence layer",
        rows: [
            {
                label: "Cited answers (not hallucinations)",
                apex: "yes",
                jira: "no",
                monday: "no",
                clickup: "no",
            },
            {
                label: "AI-drafts",
                apex: "yes",
                jira: "partial",
                monday: "partial",
                clickup: "yes",
            },
            {
                label: "Forecasts that update themselves",
                apex: "yes",
                jira: "no",
                monday: "no",
                clickup: "no",
            },
        ],
    },
    {
        icon: BarChart3,
        name: "Leadership view",
        rows: [
            {
                label: "Single company dashboard",
                apex: "yes",
                jira: "no",
                monday: "partial",
                clickup: "partial",
            },
            {
                label: "Revenue · People · Ops · Cash · Customer",
                apex: "yes",
                jira: "no",
                monday: "no",
                clickup: "no",
            },
        ],
    },
    {
        icon: DollarSign,
        name: "Pricing & access",
        rows: [
            {
                label: "Free forever for small teams",
                apex: "yes",
                jira: "partial",
                monday: "no",
                clickup: "partial",
            },
            {
                label: "AI included in core plan",
                apex: "yes",
                jira: "no",
                monday: "no",
                clickup: "no",
            },
            {
                label: "Predictable per-company pricing",
                apex: "yes",
                jira: "no",
                monday: "no",
                clickup: "no",
            },
        ],
    },
];

export const COMPARE_DEEP_DIVES = {
    jira: {
        title: "ApexBrain vs Jira",
        sub: "From engineering tickets to a whole-company system",
        story:
            "Jira is excellent at engineering issue tracking, but only engineering. The rest of the company — sales, ops, finance, HR — lives in tools that don't share Jira's schema. The result is one source of truth per silo, and zero source of truth for the company.",
        wins: [
            "All nine modules live on one graph — no separate add-ons",
            "AI Co-pilot reasons across pipeline, projects and people, with citations",
            "Workflows and approvals included natively — not via Service Desk add-on",
            "Free for teams under 8; predictable from there",
        ],
        keepFromJira: [
            "Rigid issue typing — recreate with ApexBrain custom records",
            "JQL power-users get a structured query bar in ApexBrain.Search",
            "Sprint discipline — Projects supports the full Scrum cycle",
        ],
    },
    monday: {
        title: "ApexBrain vs Monday",
        sub: "From colourful boards to a connected operating layer",
        story:
            "Monday is great where work is board-shaped. But Monday's CRM, HR and dev tools are separate products glued together with integrations. ApexBrain ships those modules on the same graph from day one.",
        wins: [
            "No paying for monday.com + monday CRM + monday Dev separately",
            "Cross-module reasoning, not cross-board automations",
            "Founder-grade reporting on Revenue, People, Ops, Customer, Cash",
            "AI Co-pilot is included, not an add-on",
        ],
        keepFromJira: [
            "Board-style views — ApexBrain.Projects ships Board, Timeline, Gantt and List out of the box",
            "Colour-coded fast scanning — applied via record themes",
            "Friendly UX patterns — ApexBrain hides the engine when you don't need it",
        ],
    },
    clickup: {
        title: "ApexBrain vs ClickUp",
        sub: "From everything-but to a system you can reason about",
        story:
            "ClickUp's surface area is enormous, but its depth is uneven and the UI shifts often. Operators end up power-users of an ever-shifting tool. ApexBrain commits to fewer pillars, deeper, with the AI Co-pilot that knows them all.",
        wins: [
            "Coherent nine-module model — not 47 features fighting each other",
            "AI Co-pilot is included on every plan",
            "Stable, semver'd APIs and UIs — no surprise rewrites",
            "Free forever under 8 seats, predictable thereafter",
        ],
        keepFromJira: [
            "Custom field flexibility — every ApexBrain record supports it",
            "Multiple view types — Board, List, Timeline, Calendar all native",
            "Doc-task interop — ApexBrain.Docs is interleaved with task threads",
        ],
    },
};
